import { Component, computed, DestroyRef, effect, inject, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment/moment';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs';

import { DefaultStatesEnum, KeyValueListType } from '@models/common';
import { RouteCodeEnum } from '@models/menu';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoDateTimeComponent } from '@shared/components/fields/cipo-date-time/cipo-date-time.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoTextComponent } from '@shared/components/fields/cipo-text/cipo-text.component';
import {
  CipoCheckboxControl,
  CipoDateTimeControl,
  CipoFieldTypes,
  CipoListControl,
  CipoTextControl,
} from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { ContextStore } from '@signal-store/context.store';
import { UserStore } from '@signal-store/index';
import { DateService, Period } from '@system/search/service/date.service';
import { SearchStore } from '@system/search/store';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'cipo-search-filter',
  templateUrl: './search-filter.component.html',
  imports: [
    CipoSelectComponent,
    CipoDateTimeComponent,
    CipoCheckboxComponent,
    FlexLayoutModule,
    CipoTextComponent,
    CommonModule,
  ],
})
export class SearchFilterComponent {
  private readonly dateService = inject(DateService);
  private readonly translateService = inject(TranslateService);
  private readonly userStore = inject(UserStore);
  private readonly searchStore = inject(SearchStore);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  readonly contextStore = inject(ContextStore);

  private modules = computed(() =>
    this.userStore
      .menuItemsList()
      .filter(
        ({ moduleId, code }) =>
          moduleId > 0 ||
          code === RouteCodeEnum.SOV ||
          code === RouteCodeEnum.ProgressPayment ||
          code === RouteCodeEnum.ChangeOrder,
      )
      .map(({ moduleId, name }) => ({ value: moduleId.toString(), name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  filterChanges = output<void>();

  form = this.fb.group(this.createControls());

  customPeriod = signal<boolean>(false);

  contractId = this.searchStore.filter.contractId();

  constructor() {
    this.onTenantChange();

    effect(() => {
      // listen for modules changes
      this.modules();

      // recreate the controls to update the modules
      untracked(() => {
        this.recreateControls();
      });
    });

    effect(() => {
      // listen for contract changed, need only for context changes
      const id = this.searchStore.filter.contractId();

      // prevent to emit changes when contract does not actually change
      if (id !== this.contractId) {
        // emit filter changed
        this.contractId = id;
        this.filterChanges.emit();

        // recreate the controls to update the contract selection
        untracked(() => {
          this.recreateControls();
        });
      }
    });
  }

  /**
   * That will update module control with new modules from tenant change
   * Also is keeping the modules selected between previous tenant and current if
   * the same module exists on both and was selected on previous tenant
   */
  private onTenantChange() {
    effect(() => {
      const newModules = this.modules().map(({ value, name }) => ({
        key: value,
        value: name,
      }));

      const moduleControl = this.form.controls.module as CipoListControl;
      moduleControl.fieldData.options = [...newModules];

      const currentSelected = (moduleControl.value as string[]) ?? [];
      const validSelected = currentSelected.filter(selected => newModules.some(module => module.key === selected));

      if (validSelected.length !== currentSelected.length) {
        const newSelected = validSelected.length > 0 ? validSelected : newModules.map(mod => mod.key);
        moduleControl.setValue(newSelected);
        this.searchStore.setFilter({ moduleIds: newSelected });
      }
    });
  }

  private listenFilterChanges() {
    this.form.controls.searchText.valueChanges
      .pipe(
        map(searchText => (Array.isArray(searchText) ? searchText[0] : searchText)),
        distinctUntilChanged(),
        debounceTime(DEBOUNCE_TIMES.default),
        tap(searchText => {
          this.searchStore.setSearchText(searchText);
          this.filterChanges.emit();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.form.controls.dateRange.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(dateRange => {
      this.searchStore.setFilter({
        startDate: moment(dateRange[0]).toDate(),
        endDate: moment(dateRange[1]).toDate(),
      });
      this.filterChanges.emit();
    });

    this.form.controls.period.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(period => {
      this.customPeriod.set(period === 'custom');
      const dates = this.dateService.getDateRange(period);
      this.searchStore.setFilter({ startDate: dates?.startDate, endDate: dates?.endDate });
      this.filterChanges.emit();
    });

    this.form.controls.matchAll.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(matchAll => {
      this.searchStore.setFilter({ matchAll });
      this.filterChanges.emit();
    });

    this.form.controls.module.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(moduleIds => {
      this.searchStore.setFilter({ moduleIds });
      this.filterChanges.emit();
    });

    this.form.controls.allContracts.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(isChecked => {
          this.contractId = isChecked ? null : this.userStore.contract().id;
          this.searchStore.setFilter({ contractId: this.contractId });
          this.filterChanges.emit();
        }),
      )
      .subscribe();

    this.form.controls.statusType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(statusTypes => {
      this.searchStore.setFilter({ statusTypes });
      this.filterChanges.emit();
    });
  }

  private createControls() {
    return {
      searchText: new CipoTextControl(this.searchStore.searchText() ?? '', {
        focused: true,
        label: 'SEARCH_AI.SEARCH_LABEL',
        placeholder: 'general.searchWaiting',
        type: CipoFieldTypes.Text,
        multiline: false,
        multiple: false,
      }),
      module: new CipoListControl(this.getSelectedModules(), {
        options: [...this.modules().map(({ value: key, name: value }) => ({ key, value }))],
        label: 'SEARCH_AI.FILTER.MODULE',
        type: CipoFieldTypes.Select,
        multiple: true,
      }),
      period: new CipoListControl(this.getSelectedPeriod(), {
        options: [...this.populatePeriods()],
        label: 'SEARCH_AI.FILTER.PERIOD',
        type: CipoFieldTypes.Select,
        hideSearch: true,
        required: true,
      }),
      dateRange: new CipoDateTimeControl(this.getDefaultDate(), {
        label: 'SEARCH_AI.FILTER.DATE_RANGE',
        type: CipoFieldTypes.DateTime,
        pickerType: 'date',
        selectMode: 'range',
      }),
      matchAll: new CipoCheckboxControl(this.searchStore.filter().matchAll ?? false, {
        label: 'SEARCH_AI.FILTER.MATCH_ALL',
        type: CipoFieldTypes.Checkbox,
      }),
      allContracts: new CipoCheckboxControl(!this.searchStore.filter().contractId, {
        label: this.translateService.instant('SEARCH_AI.FILTER.ALL_CONTRACTS', {
          moduleName: this.contextStore.contractsName(),
        }),
        type: CipoFieldTypes.Checkbox,
      }),
      statusType: new CipoListControl(this.getSelectedStatusTypes(), {
        options: [...this.populateStatusTypes()],
        label: 'SEARCH_AI.FILTER.STATUS_TYPE',
        type: CipoFieldTypes.Select,
        hideSearch: true,
        multiple: true,
      }),
    };
  }

  private recreateControls() {
    this.form = this.fb.group(this.createControls());
    this.listenFilterChanges();
  }

  private getDefaultDate() {
    return [moment().toISOString(), moment().subtract(1, 'month').toISOString()];
  }

  private getSelectedStatusTypes() {
    const statusTypes =
      this.searchStore.filter.statusTypes().length > 0
        ? this.searchStore.filter.statusTypes()
        : this.populateStatusTypes().map(statusType => statusType.key);
    this.searchStore.setFilter({ statusTypes: statusTypes });
    return statusTypes;
  }

  private getSelectedModules(): string[] {
    const filterSelectedModules = this.searchStore.filter().moduleIds ?? [];

    if (filterSelectedModules.length) {
      return filterSelectedModules;
    }

    const activeMenuItem = this.userStore.menu.activeMenuItem();

    if (activeMenuItem) {
      const moduleExist = this.modules().some(module => module.value === String(activeMenuItem.moduleId));
      if (moduleExist) {
        this.searchStore.setFilter({ moduleIds: [String(activeMenuItem.moduleId)] });
        return [String(activeMenuItem.moduleId)];
      }
    } else {
      const allModules = [...this.getAllModulesValue()];
      this.searchStore.setFilter({ moduleIds: allModules });
      return allModules;
    }
  }

  private getAllModulesValue() {
    let allModules: string[] = [];
    this.modules().forEach(module => {
      allModules.push(module.value);
    });

    return allModules;
  }

  private populatePeriods(): KeyValueListType<any>[] {
    return this.dateService.getAvailablePeriods();
  }

  private populateStatusTypes(): { key: string; value: string }[] {
    return [
      {
        key: DefaultStatesEnum[DefaultStatesEnum.Approved],
        value: DefaultStatesEnum[DefaultStatesEnum.Approved],
      },
      {
        key: DefaultStatesEnum[DefaultStatesEnum.Draft],
        value: DefaultStatesEnum[DefaultStatesEnum.Draft],
      },
      {
        key: DefaultStatesEnum[DefaultStatesEnum.Open],
        value: DefaultStatesEnum[DefaultStatesEnum.Open],
      },
      {
        key: DefaultStatesEnum[DefaultStatesEnum.Rejected],
        value: DefaultStatesEnum[DefaultStatesEnum.Rejected],
      },
    ];
  }

  private getSelectedPeriod(): Period | string {
    const filterSelectedPeriod = this.searchStore.filter().period as Period;

    if (filterSelectedPeriod) {
      this.customPeriod.set(filterSelectedPeriod === Period.custom);
      return filterSelectedPeriod;
    }

    return 'all';
  }
}
